import { yupResolver } from '@hookform/resolvers/yup'
import {
  Alert,
  Box,
  Grid,
  InputLabel,
  OutlinedInput,
  styled
} from '@mui/material'
import { ErrorText } from 'components'
import { mapLoader } from 'components/domain/GoogleMaps'
import { color } from 'config/color'
import { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'
import { type IUserValue } from '../Account'

interface IFormData {
  name: string
  portalPhone: string
  mailingAddress: string
  email: string
  mailingAddressObj?: IMailingAddress
}

export interface IMailingAddress {
  addressLine1: string
  addressLine2: string
  postal: string
  city: string
  state: string
}

const inputPlaceholderSx = {
  '& input::placeholder': {
    color: '#737373',
    opacity: 1
  }
}

const accountNameInputId = 'account-edit-name'
const accountEmailInputId = 'account-edit-email'
const accountPhoneInputId = 'account-edit-portal-phone'
const accountMailingAddressInputId = 'address-input'

const accountPhoneErrorId = 'error-account-edit-portal-phone'
const accountMailingAddressErrorId = 'error-account-edit-mailing-address'

export const EditInformationForm = ({
  userValue,
  onMailingAddressChange,
  onPortalPhoneChange
}: {
  userValue: IUserValue
  onMailingAddressChange: (mailingAddress: IMailingAddress) => void
  onPortalPhoneChange: (portalPhone: string) => void
}): JSX.Element => {
  const schema = yup.object().shape({
    portalPhone: yup
      .string()
      .matches(
        /^\(\d{3}\) \d{3}-\d{4}$/,
        'Please enter a valid US phone number in the format (123) 456-7890.'
      )
      .required('Contact number is required'),
    mailingAddress: yup.string().required('Mailing address is required')
  })
  const {
    control,
    formState: { errors },
    setValue,
    watch
  } = useForm<IFormData>({
    mode: 'all',
    defaultValues: {
      name: userValue.name ?? '',
      portalPhone: userValue.mobileNumber ?? userValue.officeNumber ?? '',
      mailingAddress: userValue.mailingAddress ?? '',
      email: userValue.email ?? ''
    },
    resolver: yupResolver(schema)
  })
  const portalPhoneValue = watch('portalPhone')

  useEffect(() => {
    mapLoader
      .importLibrary('places')
      .then((places) => {
        const addressInput = document.getElementById('address-input') as HTMLInputElement
        const autocomplete = new places.Autocomplete(addressInput, {
          componentRestrictions: { country: ['us'] },
          fields: ['address_components', 'formatted_address'],
          types: ['address']
        })
        autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace()
          const addressArray = place.address_components?.filter((item: { types: string[] }) => ['street_number', 'route', 'locality', 'political', 'administrative_area_level_1', 'postal_code'].includes(item.types[0])).map((item: { short_name: string }) => item.short_name)
          if ((addressArray != null) && addressArray.length > 0) {
            const houseNumber = addressArray[0].toUpperCase() ?? ''
            const streetName = addressArray[1].toUpperCase() ?? ''
            const city = addressArray[2].toUpperCase() ?? ''
            const state = addressArray[3].toUpperCase() ?? ''
            const postal = addressArray[4].toUpperCase() ?? ''
            const completeAddress = `${houseNumber} ${streetName}, ${city}, ${state} ${postal}`

            setValue('mailingAddress', completeAddress)

            onMailingAddressChange({
              addressLine1: `${houseNumber} ${streetName}`,
              addressLine2: '',
              postal,
              city,
              state
            })
          }
        })
      })
      .catch((e) => { console.log(e) })
  }, [onMailingAddressChange, setValue])

  useEffect(() => {
    if (
      portalPhoneValue !== userValue.mobileNumber &&
      portalPhoneValue !== userValue.officeNumber
    ) {
      onPortalPhoneChange(portalPhoneValue)
    }
  }, [portalPhoneValue])

  return (
    <>
      <form>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Box mb={2.5}>
              <FormLabel htmlFor={accountNameInputId}>NAME</FormLabel>
              <Controller
                name="name"
                control={control}
                render={({ field: { onChange, value, name } }): JSX.Element => (
                  <OutlinedInput
                    id={accountNameInputId}
                    fullWidth
                    type="text"
                    name={name}
                    placeholder="Name"
                    sx={inputPlaceholderSx}
                    onChange={onChange}
                    value={value}
                    disabled
                    inputProps={{
                      autoComplete: 'name',
                      readOnly: true
                    }}
                  />
                )}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box mb={2.5}>
              <FormLabel htmlFor={accountPhoneInputId}>HOME / OFFICE NUMBER *</FormLabel>
              <Controller
                name="portalPhone"
                control={control}
                render={({ field: { onChange, value, name } }): JSX.Element => {
                  const hasError = errors.portalPhone != null
                  return (
                    <OutlinedInput
                      id={accountPhoneInputId}
                      fullWidth
                      type="text"
                      name={name}
                      placeholder="Home / Office Number"
                      sx={inputPlaceholderSx}
                      onChange={onChange}
                      value={value}
                      inputProps={{
                        inputMode: 'tel',
                        autoComplete: 'tel',
                        'aria-invalid': hasError,
                        'aria-describedby': hasError ? accountPhoneErrorId : undefined
                      }}
                    />
                  )
                }}
              />
              {errors.portalPhone && (
                <ErrorText id={accountPhoneErrorId} role="alert">{errors.portalPhone.message}</ErrorText>
              )}
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box mb={2.5}>
              <FormLabel htmlFor={accountMailingAddressInputId}>MAILING ADDRESS *</FormLabel>
              <Controller
                name="mailingAddress"
                control={control}
                render={({ field: { onChange, value, name } }): JSX.Element => {
                  const hasError = errors.mailingAddress != null
                  return (
                    <OutlinedInput
                      id={accountMailingAddressInputId}
                      fullWidth
                      type="text"
                      name={name}
                      placeholder="Mailing Address"
                      sx={inputPlaceholderSx}
                      onChange={onChange}
                      value={value}
                      inputProps={{
                        autoComplete: 'street-address',
                        'aria-invalid': hasError,
                        'aria-describedby': hasError ? accountMailingAddressErrorId : undefined
                      }}
                    />
                  )
                }}
              />
              {errors.mailingAddress && (
                <ErrorText id={accountMailingAddressErrorId} role="alert">{errors.mailingAddress.message}</ErrorText>
              )}
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box mb={2.5}>
              <FormLabel htmlFor={accountEmailInputId}>EMAIL ADDRESS *</FormLabel>
              <Controller
                name="email"
                control={control}
                render={({ field: { onChange, value, name } }): JSX.Element => (
                  <OutlinedInput
                    id={accountEmailInputId}
                    fullWidth
                    type="text"
                    name={name}
                    placeholder="Email Address"
                    sx={inputPlaceholderSx}
                    onChange={onChange}
                    value={value}
                    disabled
                    inputProps={{
                      autoComplete: 'email',
                      readOnly: true
                    }}
                  />
                )}
              />
            </Box>
          </Grid>
        </Grid>
      </form>
      <Alert
        variant="filled"
        severity="error"
        sx={{
          backgroundColor: color.background[50],
          color: color.grey[700],
          padding: '0',
          '& .MuiAlert-icon': {
            backgroundColor: color.error[600],
            padding: '10px',
            display: 'flex',
            justifyContent: 'center'
          },
          '& .MuiAlert-icon svg': {
            fill: 'white'
          },
          '& .MuiAlert-message': {
            display: 'flex',
            alignItems: 'center'
          }
        }}
      >
        * Mandatory fields are required.
      </Alert>
    </>
  )
}

const FormLabel = styled(InputLabel)`
  color: #4d515f;
  display: block;
  font-weight: 400;
  font-size: 12px;
  line-height: 14px;
  letter-spacing: 0.02em;
  margin-bottom: 8px;
`
